import { EsaSessionData } from "@outschool/auth-shared";
import { CookieKeys } from "@outschool/data-schemas";
import { FundingProgramAbbreviation } from "@outschool/funding-programs-shared";
import Cookies from "js-cookie";

import { useSession } from "../providers/SessionProvider";
import { EsaSessionType, useTokenContext } from "../providers/TokenProvider";
import { useFundingProgramProperties } from "./useFundingProgramProperties";
import { useSelfElectedStateFundingProgram } from "./useSelfElectedStateFundingProgram";

export const getEsaSession = (): EsaSessionData => {
  return JSON.parse(Cookies.get(CookieKeys.EsaSession) || "null");
};

/**
 * Returns funding program abbreviation from cookie-based ESA sessions only.
 * Does not check currentUser (no React hooks, no async).
 * Accepts a findByInstitution lookup function to avoid direct dependency on hardcoded data.
 */
const getCookieBasedFundingProgramAbbr = (
  findByInstitution: (
    institution: string | undefined | null,
  ) => { abbreviation: string } | undefined,
): string | undefined => {
  if (Cookies.get(CookieKeys.MeritKeepSession)) {
    return FundingProgramAbbreviation.KS;
  }
  if (Cookies.get(CookieKeys.EmaSession) === "true") {
    return FundingProgramAbbreviation.EMA;
  }
  if (Cookies.get(CookieKeys.EsaSession)) {
    const esaSession = getEsaSession();
    const fundingProgram = findByInstitution(esaSession.institution);
    return fundingProgram?.abbreviation ?? esaSession?.shipping?.state;
  }
  return undefined;
};

export const useIsEsaSessionPresent = () => {
  return !!useSessionType();
};

export const esaCheckoutUrl = (
  orderSlugId: string,
  callbackUrl: string,
  vendorId: string,
  cwUrlFallback?: string,
) => {
  const esaSession = getEsaSession();
  const cwUrl = !!esaSession?.cwUrl ? esaSession.cwUrl : cwUrlFallback;
  return `${cwUrl}/payby-checkout/?callback=${callbackUrl}/api/cw/order/${orderSlugId}&vendorId=${vendorId}`;
};

export const destroyEsaSession = () => {
  Cookies.remove(CookieKeys.EsaSession);
  Cookies.remove(CookieKeys.EsaSessionType);
};

export const useFundingProgramAbbr = (): string | undefined => {
  const { currentUser } = useSession();
  const sessionType = useSessionType();
  const { findByInstitution } = useFundingProgramProperties();

  switch (sessionType) {
    case EsaSessionType.MERIT:
    case EsaSessionType.EMA:
    case EsaSessionType.CW:
      return getCookieBasedFundingProgramAbbr(findByInstitution);
    case EsaSessionType.MEMBERSHIP:
      return currentUser?.esaMembershipPrograms?.[0];
    default:
      return undefined;
  }
};

/**
 * Returns funding program abbreviation for use in GraphQL query variables.
 * Only returns values for cookie-based sessions (MERIT, EMA, CW).
 * For MEMBERSHIP users, returns undefined - the server handles this via userApprovedFundingPrograms.
 *
 * Use this hook when passing esaState to GraphQL queries to avoid refetching when currentUser loads.
 */
export const useFundingProgramAbbrForQueries = (): string | undefined => {
  const { findByInstitution } = useFundingProgramProperties();
  return getCookieBasedFundingProgramAbbr(findByInstitution);
};

export const useEsaWaitPeriod = (): number => {
  const esaState = useFundingProgramAbbr();
  const { getWaitPeriodDays } = useFundingProgramProperties();
  return getWaitPeriodDays(esaState);
};

export const useEsaEndDate = (_ignoreFirstDeadline = false): Date | null => {
  const esaState = useFundingProgramAbbr();
  const { getProgramEndDate } = useFundingProgramProperties();
  return getProgramEndDate(esaState);
};

export const useSessionType = (): EsaSessionType | null => {
  const { usesCharter } = useSelfElectedStateFundingProgram();

  const { currentUser } = useSession();
  const { authData } = useTokenContext();
  if (currentUser?.hasEsaMembership) {
    return EsaSessionType.MEMBERSHIP;
  }
  if (Cookies.get("meritKeepSession")) {
    return EsaSessionType.MERIT;
  }
  if (Cookies.get("floridaEMASession") === "true") {
    return EsaSessionType.EMA;
  }
  if (Cookies.get(CookieKeys.EsaSession)) {
    return EsaSessionType.CW;
  }
  // if we are logged in, we have set the requiresMembershipForEnrollment flag
  // so we don't need to rely on localStorage values
  // need to use AuthData because here currentUser is not available
  if (usesCharter() && !authData) {
    return EsaSessionType.SELF_IDENTIFIED_PUBLIC_FUNDING;
  }

  return null;
};
